import React, { useState } from "react";
import { ArrowDown, ArrowUp, Bot, Edit3, Plus, Trash2 } from "lucide-react";
import { useLlmKeys } from "../../hooks/useLlmKeys";
import { PERMISSIONS, usePermissions } from "../../hooks/usePermissions";
import { readApiError } from "../../utils/readApiError";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { format, messages as t } from "./AdminLlmKeys.messages";

const PROVIDERS = ["anthropic", "openai_compatible"];
const EMPTY_FORM = { displayName: "", provider: "anthropic", model: "", baseUrl: "", apiKey: "" };

export function AdminLlmKeys() {
  const { can } = usePermissions();
  const canRead = can(PERMISSIONS.LLM_READ);
  const canWrite = can(PERMISSIONS.LLM_WRITE);
  const {
    keys,
    isLoading,
    error,
    createKey,
    updateKey,
    reorderKeys,
    deleteKey,
    testKey,
    isSaving,
    isTesting,
  } = useLlmKeys({ enabled: canRead });

  // null: closed, "new": add form, otherwise the id of the key being edited.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [pending, setPending] = useState(null);
  const [actionError, setActionError] = useState(null);

  if (!canRead) return null;

  function openForm(key) {
    setEditing(key ? key.id : "new");
    setForm(
      key
        ? {
            displayName: key.display_name,
            provider: key.provider,
            model: key.model,
            baseUrl: key.base_url || "",
            apiKey: "",
          }
        : EMPTY_FORM
    );
    setFormError(null);
    setTestResult(null);
  }

  function closeForm() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setTestResult(null);
  }

  async function submitForm(event) {
    event.preventDefault();
    setFormError(null);
    try {
      if (editing === "new") {
        await createKey(form);
      } else {
        await updateKey({
          keyId: editing,
          displayName: form.displayName,
          model: form.model,
          baseUrl: form.baseUrl,
          apiKey: form.apiKey,
        });
      }
      closeForm();
    } catch (err) {
      setFormError(readApiError(err, t["admin_llm_keys.save_failed"]));
    }
  }

  async function runTest(request) {
    setTestResult(null);
    try {
      const result = await testKey(request);
      setTestResult({ keyId: request.keyId ?? editing, ...result });
    } catch (err) {
      setTestResult({ keyId: request.keyId ?? editing, failed: readApiError(err, t["admin_llm_keys.test.error"]) });
    }
  }

  async function move(index, offset) {
    const ids = keys.map((key) => key.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + offset, 0, moved);
    setActionError(null);
    try {
      await reorderKeys(ids);
    } catch (err) {
      setActionError(readApiError(err, t["admin_llm_keys.save_failed"]));
    }
  }

  async function toggle(key) {
    if (key.is_enabled) {
      setActionError(null);
      setPending({ type: "disable", key });
      return;
    }
    try {
      await updateKey({ keyId: key.id, isEnabled: true });
    } catch (err) {
      setActionError(readApiError(err, t["admin_llm_keys.save_failed"]));
    }
  }

  async function confirmPending(reason) {
    try {
      if (pending.type === "disable") {
        await updateKey({ keyId: pending.key.id, isEnabled: false, reason });
      } else {
        await deleteKey({ keyId: pending.key.id, reason });
      }
      setPending(null);
      setActionError(null);
    } catch (err) {
      setActionError(readApiError(err, t["admin_llm_keys.save_failed"]));
    }
  }

  const testLine = (keyId) =>
    testResult && testResult.keyId === keyId ? <TestResult result={testResult} /> : null;

  return (
    <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-2xl bg-[#C0F6B7]/40 text-[#0A4730] grid place-items-center shrink-0">
            <Bot size={20} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-serif font-semibold text-[#0A4730]">
              {t["admin_llm_keys.title"]}
            </h3>
            <p className="text-xs text-slate-400">{t["admin_llm_keys.subtitle"]}</p>
          </div>
        </div>
        {canWrite && editing === null && (
          <button
            type="button"
            onClick={() => openForm(null)}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0A4730] px-4 py-2 text-xs font-semibold text-white"
          >
            <Plus size={14} /> {t["admin_llm_keys.add"]}
          </button>
        )}
      </div>

      {(error || (actionError && !pending)) && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          {actionError || t["admin_llm_keys.load_failed"]}
        </div>
      )}

      {editing === "new" && (
        <KeyForm
          form={form}
          isNew
          onChange={setForm}
          onSubmit={submitForm}
          onCancel={closeForm}
          onTest={() => runTest(form)}
          saving={isSaving}
          testing={isTesting}
          error={formError}
          testLine={testLine("new")}
        />
      )}

      {!isLoading && keys.length === 0 && editing !== "new" && (
        <p className="text-sm text-slate-400">{t["admin_llm_keys.empty"]}</p>
      )}

      <ol className="space-y-3">
        {keys.map((key, index) =>
          editing === key.id ? (
            <li key={key.id}>
              <KeyForm
                form={form}
                onChange={setForm}
                onSubmit={submitForm}
                onCancel={closeForm}
                onTest={() => runTest({ keyId: key.id })}
                saving={isSaving}
                testing={isTesting}
                error={formError}
                testLine={testLine(key.id)}
              />
            </li>
          ) : (
            <li
              key={key.id}
              className="rounded-2xl border border-slate-100 p-4 flex flex-col md:flex-row md:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0A2540]">
                  {index + 1}. {key.display_name}
                </p>
                <p className="text-xs text-slate-500">
                  {t[`admin_llm_keys.provider.${key.provider}`]} · {key.model} ·{" "}
                  {format("admin_llm_keys.key_hint", { hint: key.key_hint })}
                </p>
                <p className="text-xs text-slate-400">
                  {format("admin_llm_keys.usage", key.usage_this_month)}
                </p>
                {testLine(key.id)}
              </div>
              <span
                className={`text-xs font-medium ${key.is_enabled ? "text-emerald-600" : "text-slate-400"}`}
              >
                {t[`admin_llm_keys.status.${key.is_enabled ? "enabled" : "disabled"}`]}
              </span>
              {canWrite && (
                <div className="flex items-center gap-1">
                  <IconButton
                    label={format("admin_llm_keys.action.move_up", { name: key.display_name })}
                    disabled={index === 0 || isSaving}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp size={14} />
                  </IconButton>
                  <IconButton
                    label={format("admin_llm_keys.action.move_down", { name: key.display_name })}
                    disabled={index === keys.length - 1 || isSaving}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown size={14} />
                  </IconButton>
                  <TextButton onClick={() => toggle(key)} disabled={isSaving}>
                    {t[`admin_llm_keys.action.${key.is_enabled ? "disable" : "enable"}`]}
                  </TextButton>
                  <TextButton onClick={() => runTest({ keyId: key.id })} disabled={isTesting}>
                    {t["admin_llm_keys.action.test"]}
                  </TextButton>
                  <IconButton label={t["admin_llm_keys.action.edit"]} onClick={() => openForm(key)}>
                    <Edit3 size={14} />
                  </IconButton>
                  <IconButton
                    label={t["admin_llm_keys.action.delete"]}
                    onClick={() => {
                      setActionError(null);
                      setPending({ type: "delete", key });
                    }}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                </div>
              )}
            </li>
          )
        )}
      </ol>

      <ConfirmActionModal
        open={Boolean(pending)}
        title={
          pending
            ? format(`admin_llm_keys.confirm.${pending.type}.title`, { name: pending.key.display_name })
            : ""
        }
        description={pending ? t[`admin_llm_keys.confirm.${pending.type}.body`] : undefined}
        confirmLabel={pending ? t[`admin_llm_keys.action.${pending.type}`] : undefined}
        destructive
        requireReason
        error={actionError}
        isSubmitting={isSaving}
        onConfirm={confirmPending}
        onCancel={() => {
          setPending(null);
          setActionError(null);
        }}
      />
    </section>
  );
}

function KeyForm({ form, isNew = false, onChange, onSubmit, onCancel, onTest, saving, testing, error, testLine }) {
  const set = (field) => (event) => onChange({ ...form, [field]: event.target.value });
  const canTest = isNew ? Boolean(form.model && form.apiKey) : true;

  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 p-4 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t["admin_llm_keys.field.display_name"]}>
          <input required value={form.displayName} onChange={set("displayName")} className={INPUT} />
        </Field>
        <Field label={t["admin_llm_keys.field.provider"]}>
          <select value={form.provider} onChange={set("provider")} disabled={!isNew} className={INPUT}>
            {PROVIDERS.map((provider) => (
              <option key={provider} value={provider}>
                {t[`admin_llm_keys.provider.${provider}`]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t["admin_llm_keys.field.model"]}>
          <input required value={form.model} onChange={set("model")} className={INPUT} />
        </Field>
        <Field label={t["admin_llm_keys.field.base_url"]} hint={t["admin_llm_keys.field.base_url.hint"]}>
          <input type="url" value={form.baseUrl} onChange={set("baseUrl")} className={INPUT} />
        </Field>
        <Field label={t["admin_llm_keys.field.api_key"]}>
          <input
            type="password"
            autoComplete="off"
            required={isNew}
            placeholder={isNew ? "" : t["admin_llm_keys.field.api_key.keep"]}
            value={form.apiKey}
            onChange={set("apiKey")}
            className={INPUT}
          />
        </Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {testLine}
      <div className="flex gap-2 justify-end">
        <TextButton onClick={onCancel}>{t["admin_llm_keys.action.cancel"]}</TextButton>
        <TextButton onClick={onTest} disabled={!canTest || testing}>
          {t["admin_llm_keys.action.test"]}
        </TextButton>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-[#0A4730] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
        >
          {t["admin_llm_keys.action.save"]}
        </button>
      </div>
    </form>
  );
}

function TestResult({ result }) {
  if (result.failed) return <p className="text-xs text-red-600">{result.failed}</p>;
  if (result.ok) return <p className="text-xs text-emerald-600">{t["admin_llm_keys.test.ok"]}</p>;
  return (
    <p className="text-xs text-red-600">
      {format("admin_llm_keys.test.failed", {
        kind: result.error_kind,
        code: result.provider_code || result.provider_status || "-",
      })}
    </p>
  );
}

const INPUT =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-[#0A2540] disabled:bg-slate-50";

function Field({ label, hint, children }) {
  return (
    <label className="block text-xs font-medium text-slate-600 space-y-1">
      <span>{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function TextButton({ children, ...props }) {
  return (
    <button
      type="button"
      className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-[#0A2540] disabled:opacity-50"
      {...props}
    >
      {children}
    </button>
  );
}

function IconButton({ label, children, ...props }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="rounded-lg p-2 text-slate-500 hover:bg-slate-50 disabled:opacity-30"
      {...props}
    >
      {children}
    </button>
  );
}
